import ApiErrorDetails from 'components/ApiErrorDetails/index.tsx';
import { Button, Card } from 'nhsuk-react-components';
import useFetchTeamRuleLibrary from 'queries/fetch/fetchTeamLibraryRules.ts';
import { Fragment, MouseEvent } from 'react';
import {
  Link, useNavigate, useParams,
} from 'react-router-dom';
import { RuleType } from 'schemas/IterationRule.schema.ts';
import useCampaignNavigate from 'hooks/useCampaignNavigate.ts';
import { LibraryRuleMetadata } from 'schemas/LibraryRule.schema.ts';
import { buildRulePathParam } from 'utils/navigationUtils.ts';
import { buildCreateNewRulePath, groupLibraryRulesByCategory } from 'utils/ruleUtils.ts';
import ProgressIndicator from 'components/ProgressIndicator';
import useUserAuthorisation from 'hooks/useUserAuthorisation';
import { RuleLocationState } from 'pages/campaign/iteration/[iterationId]/Rule';

class NoLibraryRulesError extends Error {
  constructor() {
    super('There are no library rules of the selected type for this team. You can add custom rules instead.');
  }
}

export default function RuleLibrarySelect({ ruleType }: { ruleType: RuleType }) {
  const {
    teamId, iterationId, andClauseNumber, ruleId,
  } = useParams();
  const navigate = useNavigate();
  const { getCampaignPathFor } = useCampaignNavigate();
  const baseCreateNewRulePath = getCampaignPathFor(`iteration/${iterationId}/${buildRulePathParam(ruleType)}`);
  const createNewRulePath = buildCreateNewRulePath(baseCreateNewRulePath, ruleId, andClauseNumber);

  const teamRuleList = useFetchTeamRuleLibrary({
    teamId: teamId!,
  });
  const source = teamRuleList?.data;
  const rulesList = Array.isArray(source) ? source.filter((rule) => rule.Type.includes(ruleType)) : [];
  const rulesByCategory = groupLibraryRulesByCategory(rulesList);
  const noAvailableRulesError = (!teamRuleList?.isFetching && rulesList.length < 1)
    ? new NoLibraryRulesError()
    : null;

  const { canCreateCustomRules } = useUserAuthorisation();

  const handleAddRule = (e: MouseEvent<HTMLAnchorElement>, libraryRule: LibraryRuleMetadata) => {
    e.preventDefault();
    navigate(createNewRulePath, {
      state: {
        libraryRule: {
          ...libraryRule,
          AttributeTarget: '~',
        },
        addCustomRule: false,
      } satisfies RuleLocationState,
    });
  };

  const handleAddCustomRule = () => {
    if (andClauseNumber !== undefined) {
      navigate(createNewRulePath, { state: { addCustomRule: true } satisfies RuleLocationState });
    } else {
      navigate('../new', { state: { addCustomRule: true } satisfies RuleLocationState });
    }
  };

  return (
    <>
      <Card>
        <>
          {teamRuleList?.isFetching ? <ProgressIndicator text="Loading rule library" /> : <ApiErrorDetails apiError={noAvailableRulesError} />}
          {rulesByCategory.map(([categoryName, rules]) => (
            <section key={categoryName}>
              <h5 className="nhsuk-u-margin-bottom-2">{categoryName}</h5>
              {rules.map((rule) => (
                <Fragment key={`${categoryName}-${rule.Name}`}>
                  <Link className="nhsuk-body-m nhsuk-u-margin-bottom-2" to="#" onClick={(e) => handleAddRule(e, rule)}>
                    {rule.Name}
                  </Link>
                  <p>{rule.LibraryDescription}</p>
                </Fragment>
              ))}
            </section>
          ))}
        </>
      </Card>

      {canCreateCustomRules() && (
        <>
          <Card>
            <p>If you cannot find a suitable rule in the library, you can add a custom rule.</p>
          </Card>
          <Button className="nhsuk-button--secondary-solid" onClick={handleAddCustomRule}>
            Add custom rule
          </Button>
        </>
      )}
    </>
  );
}
